"use client";

import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import Button from "@/components/ui/Button";
import { fadeInUp } from "@/lib/animations";

interface EventSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function EventSuccessModal({
  isOpen,
  onClose,
}: EventSuccessModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        variants={fadeInUp}
        initial="hidden"
        animate="visible"
        exit="hidden"
        className="relative z-10 w-full max-w-md"
      >
        <div className="bg-card rounded-2xl p-8 shadow-2xl border border-border">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text" />
          </button>

          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center"
            >
              <Check className="w-10 h-10 text-green-600" />
            </motion.div>
          </div>

          {/* Content */}
          <div className="text-center mb-6">
            <h3 className="text-heading text-2xl font-bold mb-3">
              Request Submitted!
            </h3>
            <p className="text-text text-sm leading-relaxed">
              Thank you for your event request. Our team will review your
              submission and contact you within 24-48 hours to discuss the
              details and next steps.
            </p>
          </div>

          {/* Action Button */}
          <Button variant="primary" className="w-full" onClick={onClose}>
            Got it!
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
